'use client';

import Image from 'next/image';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { staticMediaStoreBaseURL } from '@/config/common';
import { REVIEWS } from '@/config/landingPage';

const ImageWithSkeleton = ({
  alt,
  className = '',
  height = 64,
  src,
  width = 64
}) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const handleLoad = useCallback(() => setLoaded(true), []);
  const handleError = useCallback(() => setErrored(true), []);

  const displaySource =
    !src || errored
      ? `${staticMediaStoreBaseURL}/icons/no-image-found.png`
      : src;

  return (
    <div
      className={`relative w-full h-full ${
        loaded ? '' : 'animate-pulse bg-gray-100'
      } ${className}`}
    >
      <Image
        alt={alt}
        className={`object-contain w-full h-full transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        height={height}
        onError={handleError}
        onLoad={handleLoad}
        src={displaySource}
        width={width}
      />
    </div>
  );
};

const CarouselItem = React.memo(({ item, isCenter, assignRef }) => (
  <div
    className={`carousel-item shrink-0 transform-gpu transition-transform duration-500 ease-out ${
      isCenter
        ? 'carousel-item--active border border-brand rounded'
        : 'carousel-item--inactive border border-gray rounded'
    }`}
    ref={assignRef}
  >
    <div className='w-12 h-12 md:w-16 md:h-16 aspect-square flex items-center justify-center'>
      <ImageWithSkeleton
        alt={`${item.role} logo`}
        className='rounded'
        height={64}
        src={`${staticMediaStoreBaseURL}/landing-page${item.logo}`}
        width={64}
      />
    </div>
  </div>
));
CarouselItem.displayName = 'CarouselItem';

const usePartnerCarousel = ({ autoScrollDuration = 10000, reviews }) => {
  const reviewCount = reviews.length;
  const middleOffset = reviewCount;
  const extended = useMemo(
    () => [...reviews, ...reviews, ...reviews],
    [reviews]
  );

  const centerStart = Math.floor(reviewCount / 2);
  const [baseIndex, setBaseIndex] = useState(centerStart);
  const baseIndexRef = useRef(baseIndex);
  const [activeExtendedIndex, setActiveExtendedIndex] = useState(
    centerStart + middleOffset
  );
  const activeExtendedIndexRef = useRef(activeExtendedIndex);

  const railRef = useRef(null);
  const itemRefs = useRef([]);

  const scrollToExtendedIndex = useCallback((extendedIndex, smooth = true) => {
    const rail = railRef.current;
    const node = itemRefs.current[extendedIndex];
    if (!rail || !node) return;
    const railRect = rail.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    const currentScroll = rail.scrollLeft;
    const nodeCenterRelativeToRail =
      nodeRect.left - railRect.left + nodeRect.width / 2 + currentScroll;
    const target = nodeCenterRelativeToRail - railRect.width / 2;
    rail.scrollTo({
      left: Math.max(0, target),
      behavior: smooth ? 'smooth' : 'auto'
    });
  }, []);

  useEffect(() => {
    baseIndexRef.current = baseIndex;
  }, [baseIndex]);

  useEffect(() => {
    activeExtendedIndexRef.current = activeExtendedIndex;
  }, [activeExtendedIndex]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const startExt = centerStart + middleOffset;
      setActiveExtendedIndex(startExt);
      setBaseIndex(centerStart);
      scrollToExtendedIndex(startExt, false);
    }, 60);
    return () => clearTimeout(timeout);
  }, [centerStart, middleOffset, scrollToExtendedIndex]);

  useEffect(() => {
    if (!autoScrollDuration || reviewCount === 0) {
      return () => {};
    }

    const interval = setInterval(() => {
      const previous = baseIndexRef.current;
      const nextBase = (previous + 1) % reviewCount;
      baseIndexRef.current = nextBase;
      setBaseIndex(nextBase);

      const targetExtendedIndex = nextBase + middleOffset;

      if (activeExtendedIndexRef.current >= 2 * reviewCount) {
        setActiveExtendedIndex(targetExtendedIndex);
        scrollToExtendedIndex(targetExtendedIndex, false);
      } else {
        setActiveExtendedIndex(targetExtendedIndex);
        scrollToExtendedIndex(targetExtendedIndex, true);
      }
    }, autoScrollDuration);

    return () => clearInterval(interval);
  }, [reviewCount, middleOffset, autoScrollDuration, scrollToExtendedIndex]);

  return { activeExtendedIndex, baseIndex, extended, itemRefs, railRef };
};

const PartnerSuccessStories = ({ autoScrollDuration = 10000 }) => {
  const { activeExtendedIndex, baseIndex, extended, itemRefs, railRef } =
    usePartnerCarousel({ autoScrollDuration, reviews: REVIEWS });

  const activeItem = useMemo(() => REVIEWS[baseIndex] ?? null, [baseIndex]);

  const carouselItems = useMemo(
    () =>
      extended.map((item, extendedIndex) => (
        <CarouselItem
          assignRef={(element) => {
            itemRefs.current[extendedIndex] = element;
          }}
          extendedIndex={extendedIndex}
          isCenter={activeExtendedIndex === extendedIndex}
          item={item}
          key={`${item.id}-${extendedIndex}`}
        />
      )),
    [extended, activeExtendedIndex, itemRefs]
  );

  return (
    <section className='w-full pb-14 sm:pb-16'>
      <div className='mx-auto max-w-7xl px-4 md:px-12 lg:px-20'>
        <div className='text-center max-w-4xl mx-auto'>
          <div className='text-2xl sm:text-2xl md:text-4xl font-extrabold leading-tight text-neutral-800'>
            Partner Success{' '}
            <span className='bg-brand-gradient bg-clip-text text-transparent'>
              Stories
            </span>
          </div>
          <div className='mt-3 sm:mt-4 text-sm sm:text-base md:text-xl text-neutral-600'>
            Across the UAE, Hafla partners are unlocking new business and
            growing with confidence.
          </div>
          <div className='mt-4 sm:mt-6 flex items-center justify-center gap-2'>
            <span className='h-1 w-20 sm:w-24 rounded-full bg-brand-gradient' />
            <span className='h-1 w-6 rounded-full bg-brand-gradient' />
          </div>
        </div>

        <div className='mx-auto max-w-7xl mt-8 sm:mt-10'>
          <div className='testimonial-grid relative rounded-3xl border border-rose-100 bg-white/50 p-6 sm:p-8 lg:p-10 shadow-lg overflow-hidden'>
            <div className='absolute testimonial-glow testimonial-glow--tl pointer-events-none' />
            <div className='absolute testimonial-glow testimonial-glow--br pointer-events-none' />

            <div className='relative flex flex-col items-center gap-6 z-10'>
              <div className='max-w-4xl mx-auto text-center relative'>
                <div
                  className={`text-gray-800 text-lg xs:text-xs md:text-2xl leading-relaxed font-medium relative z-10 xs:h-28 md:h-40 content-center`}
                >
                  {activeItem?.quote}
                </div>
                <div className='text-base sm:text-sm md:text-base mt-6 relative z-10'>
                  <div className='font-semibold text-gray-900'>
                    {activeItem?.name}
                  </div>
                  <div className='mt-1 text-xs md:text-sm text-gray-500'>
                    {activeItem?.role}
                  </div>
                </div>
              </div>

              <div className='w-full relative mt-4'>
                <div
                  className='relative overflow-x-auto no-scrollbar py-6 xs:max-w-xs sm:max-w-xs md:max-w-lg mx-auto'
                  ref={railRef}
                >
                  <div className='flex items-end gap-3 px-8 md:px-12'>
                    <div className='shrink-0 min-w-[28%] md:min-w-[20%]' />
                    {carouselItems}
                    <div className='shrink-0 min-w-[28%] md:min-w-[20%]' />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnerSuccessStories;
